import { createRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import type { LeaveType } from '@kora/types';
import { meLayoutRoute } from './meLayout.js';
import { getMeProfile, listMyPayslips } from '../api/me.js';
import { getMyLeaveBalance } from '../api/leave.js';
import { formatDate, formatMoney } from '../lib/format.js';
import { Alert } from '../components/Alert.js';

/**
 * /me — employee self-service dashboard. Tiles linking into the portal:
 * latest payslip, profile, documents, leave balance, loans, attendance,
 * performance.
 *
 * Visible only to role=employee. The /me layout route's beforeLoad bounces
 * other roles to /dashboard before this component mounts.
 */

const LEAVE_LABEL: Record<LeaveType, string> = {
  annual: 'Annual', sick: 'Sick', maternity: 'Maternity', paternity: 'Paternity',
  compassionate: 'Compassionate', unpaid: 'Unpaid', casual: 'Casual', study: 'Study', other: 'Other',
};
// ADR-0023 Phase 3a — a leaveType may be an org-defined custom key. Label via
// built-in map, else humanize the key (e.g. "exam_leave" → "Exam Leave").
const humanizeLeaveKey = (k: string): string => k.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const leaveLabel = (key: string): string => (LEAVE_LABEL as Record<string, string>)[key] ?? humanizeLeaveKey(key);
export const meIndexRoute = createRoute({
  getParentRoute: () => meLayoutRoute,
  path: '/me',
  component: MeIndexPage,
});

function MeIndexPage(): JSX.Element {
  const profileQuery = useQuery({
    queryKey: ['me', 'profile'],
    queryFn: getMeProfile,
  });
  const payslipsQuery = useQuery({
    queryKey: ['me', 'payslips'],
    queryFn: listMyPayslips,
  });
  const leaveQuery = useQuery({
    queryKey: ['me', 'leave', 'balance'],
    queryFn: getMyLeaveBalance,
  });

  if (profileQuery.isLoading) {
    return (
      <section className="p-8">
        <div className="rounded border border-gray-900 p-8 text-center text-gray-500">
          Loading...
        </div>
      </section>
    );
  }
  if (profileQuery.isError || !profileQuery.data) {
    return (
      <section className="p-8">
        <Alert variant="error">
          Could not load your profile. Try refreshing — if the problem
          persists, contact HR.
        </Alert>
      </section>
    );
  }

  const { employee } = profileQuery.data;
  const latest = payslipsQuery.data?.payslips[0];

  return (
    <section className="p-8 space-y-6 max-w-3xl">
      <header className="space-y-2">
        <h1 className="font-serif text-3xl">
          Hi, {employee.firstName}
          {employee.middleName ? ` ${employee.middleName}` : ''}
        </h1>
        <p className="text-sm text-gray-400">
          Employee <span className="font-mono">{employee.employeeNumber}</span>
          {employee.position ? ` · ${employee.position}` : ''}
          {employee.department ? ` · ${employee.department}` : ''}
          {' · '}
          {employee.countryCode}
        </p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Tile title="Latest payslip">
          {payslipsQuery.isLoading ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : latest ? (
            <div className="space-y-2">
              <p className="text-sm text-gray-400">
                {formatDate(latest.periodStart)} → {formatDate(latest.periodEnd)}
              </p>
              <p className="font-mono text-2xl">
                {formatMoney(latest.netSalary, latest.currency)}
              </p>
              <p className="text-xs text-gray-500">
                Net pay · v{latest.version}
                {latest.runStatus === 'voided' ? ' · voided' : ''}
              </p>
              <Link
                to="/me/payslips"
                className="inline-block text-xs text-white underline underline-offset-2 mt-2"
              >
                See all payslips →
              </Link>
            </div>
          ) : (
            <p className="text-sm text-gray-500">
              No payslips yet. They&apos;ll appear here after your first
              payroll cycle is approved by HR.
            </p>
          )}
        </Tile>

        <Tile title="My profile">
          <p className="text-sm text-gray-400">
            {employee.email ?? '—'}
            {employee.phone ? <br /> : null}
            {employee.phone ?? null}
          </p>
          <Link
            to="/me/profile"
            className="inline-block text-xs text-white underline underline-offset-2 mt-3"
          >
            View and edit my profile →
          </Link>
        </Tile>

        <Tile title="My documents">
          <p className="text-sm text-gray-400">
            Contracts, certificates, IDs, and other files HR has on
            record for you.
          </p>
          <Link
            to="/me/documents"
            className="inline-block text-xs text-white underline underline-offset-2 mt-3"
          >
            View all documents →
          </Link>
        </Tile>

        <Tile title="My leave">
          {leaveQuery.isLoading ? (
            <p className="text-sm text-gray-500">Loading…</p>
          ) : leaveQuery.isError ? (
            <p className="text-sm text-gray-500">Leave balance, requests, and history.</p>
          ) : (leaveQuery.data?.balances ?? []).length > 0 ? (
            <ul className="space-y-1">
              {leaveQuery.data!.balances.map((b) => (
                <li key={b.leaveType} className="flex items-baseline justify-between text-sm">
                  <span className="text-gray-400">{leaveLabel(b.leaveType)}</span>
                  <span className="text-white tabular-nums">
                    {b.available.toFixed(1)}<span className="text-gray-500 text-xs"> days</span>
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-500">No leave balance yet.</p>
          )}
          <Link
            to="/me/leave"
            className="inline-block text-xs text-white underline underline-offset-2 mt-3"
          >
            Request leave + see history →
          </Link>
        </Tile>

        <Tile title="My loans">
          <p className="text-sm text-gray-400">
            Salary advances and loans, your deduction schedule, and repayment progress.
          </p>
          <Link
            to="/me/loans"
            className="inline-block text-xs text-white underline underline-offset-2 mt-3"
          >
            View my loans →
          </Link>
        </Tile>

        <Tile title="My attendance">
          <p className="text-sm text-gray-400">
            Clock in and out, and review your timesheets and overtime.
          </p>
          <Link
            to="/me/attendance"
            className="inline-block text-xs text-white underline underline-offset-2 mt-3"
          >
            Go to attendance →
          </Link>
        </Tile>

        <Tile title="My performance">
          <p className="text-sm text-gray-400">
            Your goals, self-assessment, and finalized reviews.
          </p>
          <Link
            to="/me/performance"
            className="inline-block text-xs text-white underline underline-offset-2 mt-3"
          >
            Go to performance →
          </Link>
        </Tile>
      </div>
    </section>
  );
}

function Tile({
  title,
  tag,
  disabled,
  children,
}: {
  title: string;
  tag?: string;
  disabled?: boolean;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div
      className={`rounded border border-gray-900 p-5 ${
        disabled ? 'bg-gray-950/40' : 'bg-gray-950'
      }`}
    >
      <header className="flex items-center justify-between mb-3">
        <h2
          className={`text-xs uppercase tracking-wide ${
            disabled ? 'text-gray-600' : 'text-gray-400'
          }`}
        >
          {title}
        </h2>
        {tag ? (
          <span className="text-[10px] text-gray-700 uppercase">{tag}</span>
        ) : null}
      </header>
      {children}
    </div>
  );
}
